import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useRevalidator, useRouteLoaderData } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { TenantSignupMode, UpdateTenantRequest } from '@/api/types';
import { TENANT_SIGNUP_MODES } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCurrentOperator } from '@/auth/AuthContext';
import { TenantCaptcha } from './TenantCaptcha';
import { TenantGhlIntegration } from './TenantGhlIntegration';
import { TenantPlanSection } from './TenantPlanSection';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

interface FormValues {
  name: string;
  signupMode: TenantSignupMode;
}

export function TenantSettings() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  // SASADM-0070 — external-auth tenants have no saas-auth signup mode; hide the
  // field. The form keeps signupMode registered (default from tenant.signupMode)
  // so the composed UpdateTenantRequest still carries it — hidden, not dropped.
  const product = tenant.productId
    ? data.products.find((p) => p.id === tenant.productId)
    : null;
  const isExternal = product?.authMode === 'EXTERNAL';
  const { t } = useCopy();
  const revalidator = useRevalidator();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const isPlatformTenant = tenant.slug === 'saas-admin';

  const form = useForm<FormValues>({
    defaultValues: {
      name: tenant.name,
      signupMode: tenant.signupMode,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setPending(true);
    setError(null);
    // Split-save safety (spec 0045): the email-config fields now live on the Email
    // Templates tab. Compose a FULL UpdateTenantRequest from the loaded tenant,
    // overriding only the fields this tab edits, so the email values are preserved
    // regardless of the backend's merge-vs-replace semantics.
    const payload: UpdateTenantRequest = {
      name: values.name,
      signupMode: values.signupMode,
      emailTemplatesDir: tenant.emailTemplatesDir ?? null,
      emailFromName: tenant.emailFromName ?? null,
      emailFromAddress: tenant.emailFromAddress ?? null,
      emailSupportEmail: tenant.emailSupportEmail ?? null,
    };
    try {
      await apiAdmin.tenants.update(tenant.id, payload);
      snack.success(t('snackbar.tenantUpdated', 'Tenant updated.'));
      revalidator.revalidate();
    } catch (err) {
      if (err instanceof ApiError) setError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setPending(false);
    }
  });

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h2" component="h2" gutterBottom>
        {t('tenants.settings.title', 'Settings')}
      </Typography>
      {isPlatformTenant ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          The platform tenant is special; some actions are disabled.
        </Alert>
      ) : null}
      <ErrorBanner error={error} />
      <Box component="form" onSubmit={onSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('tenants.settings.name', 'Name')}
              required
              {...form.register('name', { required: true, maxLength: 200 })}
              error={Boolean(form.formState.errors.name)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('tenants.settings.slug', 'Slug')}
              value={tenant.slug}
              disabled
              helperText={t('tenants.settings.slugHelper', '')}
            />
          </Grid>
          {/* SASADM-0070 — signup-mode field hidden for external-auth tenants. The
              form still submits signupMode (registered above), so the payload keeps it. */}
          {!isExternal && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                control={form.control}
                name="signupMode"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label={t('tenants.settings.signupMode', 'Signup mode')}
                    disabled={isPlatformTenant}
                  >
                    {TENANT_SIGNUP_MODES.map((m) => (
                      <MenuItem key={m} value={m}>
                        {t(`tenants.signupMode.${m}`, m)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          )}
        </Grid>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            mt: 3,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={pending || !form.formState.isDirty}
            startIcon={pending ? <CircularProgress size={16} /> : null}
          >
            {t('common.save', 'Save')}
          </Button>
        </Stack>
      </Box>
      <Box sx={{ mt: 4 }}>
        <Typography variant="h3" component="h3" gutterBottom>
          {t('tenants.settings.localeSection', 'Localization')}
        </Typography>
        <Alert severity="info">{t('tenants.settings.localeNotice', '')}</Alert>
      </Box>
      {/* GHL integration (spec 0045 / saas-auth SASATH-0053) — SUPER_ADMIN only,
          in the real-estate the email fields vacated (U1). Gated here the same
          way the OIDC tab is; the component also degrades to a contained
          "SUPER_ADMIN required" state if the API returns 403. */}
      {/* Turnstile / Captcha (spec SASADM-0086 / saas-auth SASATH-0149/0151/0152) —
          SUPER_ADMIN only, immediately above the GHL section. Gated here the same
          way; the component also degrades to a contained "Super admin required"
          state if either self-fetch returns 403. */}
      {operator?.isSuperAdmin ? <TenantCaptcha tenantId={tenant.id} /> : null}
      {operator?.isSuperAdmin ? <TenantGhlIntegration tenantId={tenant.id} /> : null}
      {/* Resource-limit plan (spec 0050 U4 / saas-auth SASATH-0062, reconciled by
          SASADM-0051) — SUPER_ADMIN only, beside the GHL section. Gated here the
          same way; the component also degrades to a contained "SUPER_ADMIN
          required" state on a 403. The section self-reads the tenant's current
          plan via the dedicated `tenantPlan.get` endpoint. */}
      {operator?.isSuperAdmin ? (
        <TenantPlanSection tenantId={tenant.id} productId={tenant.productId} />
      ) : null}
    </Paper>
  );
}
